/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { Button } from '@carbon/react';
import { ChevronRight } from '@carbon/icons-react';
import { ChevronDown } from '@carbon/react/icons';

import { FeelPlaygroundPanel } from './FeelPlaygroundPanel';

/**
 * @typedef {import('../playground-panel/FeelPlaygroundPanel').FeelPlaygroundPanelProps} FeelPlaygroundPanelProps
 */

/**
 * @typedef {Object} FeelPlaygroundCollapsiblePanelProps
 * @property {boolean} isCollapsed - Whether the panel is collapsed
 * @property {() => void} onToggleCollapsed - Callback when the collapse state is changed
 * @property {React.ReactNode} [children] - Content to display in the panel
 * @property {React.ReactNode} [headerItems] - Additional items to display in the header
 */

/**
 * A panel that adds collapse/expand functionality to a FeelPlaygroundPanel
 * @param {Omit<FeelPlaygroundPanelProps, 'headerItems'> & FeelPlaygroundCollapsiblePanelProps} props
 * @returns {JSX.Element}
 */
export const FeelPlaygroundCollapsiblePanel = ({
  isCollapsed,
  onToggleCollapsed,
  children,
  headerItems,
  title,
  ...rest
}) => {
  const actionName = isCollapsed ? 'Show' : 'Hide';

  const collapseButton = (
    <Button
      hasIconOnly
      tooltipPosition={'right'}
      kind="ghost"
      size="sm"
      renderIcon={() => (isCollapsed ? <ChevronRight /> : <ChevronDown />)}
      iconDescription={actionName}
      aria-label={actionName + ' ' + title}
      onClick={(e) => {
        e.stopPropagation();
        onToggleCollapsed();
      }}
    />
  );

  return (
    <FeelPlaygroundPanel
      isCollapsed={isCollapsed}
      onHeaderClick={onToggleCollapsed}
      headerItems={
        <>
          {headerItems}
          {collapseButton}
        </>
      }
      title={title}
      {...rest}
    >
      {!isCollapsed && children}
    </FeelPlaygroundPanel>
  );
};
